import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import WaitlistModal from "@/components/WaitlistModal";

const SECTIONS = [
  { id: "introduction", title: "1. Introduction" },
  { id: "data-we-collect", title: "2. Data We Collect" },
  { id: "how-we-use-data", title: "3. How We Use Your Data" },
  { id: "legal-bases", title: "4. Legal Bases for Processing" },
  { id: "admin-visibility", title: "5. Admin Visibility of Project Data" },
  { id: "ai-processing", title: "6. AI Processing & Sub-Processors" },
  { id: "international-transfers", title: "7. International Data Transfers" },
  { id: "data-retention", title: "8. Data Retention" },
  { id: "cookies", title: "9. Cookies and Similar Technologies" },
  { id: "electronic-communications", title: "10. Electronic Communications" },
  { id: "your-rights", title: "11. Your Rights" },
  { id: "security-breach", title: "12. Security and Breach Notification" },
  { id: "children", title: "13. Children" },
  { id: "contact-grievance", title: "14. Contact and Grievance Redressal" },
  { id: "changes", title: "15. Changes to This Policy" },
];

export default function PrivacyPolicy() {
    const [showWaitlist, setShowWaitlist] = useState(false);
    const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            {
                rootMargin: "-100px 0px -70% 0px",
                threshold: 0,
            }
        );

        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const yOffset = -88; // account for fixed header
            const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[#F8FAFC] text-[#0F1F3D]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/logo.png"
                            alt="LaunchRadar"
                            className="w-[200px]"
                        />
                    </Link>

                    <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
                        <Link to="/" className="hover:text-[#0F1F3D] transition-colors">
                            Use cases
                        </Link>
                        <Link to="/" className="hover:text-[#0F1F3D] transition-colors">
                            How it works
                        </Link>
                        <Link to="/" className="hover:text-[#0F1F3D] transition-colors">
                            Why us
                        </Link>
                        <Link
                            to="/contact"
                            className="hover:text-[#0F1F3D] transition-colors"
                        >
                            Contact
                        </Link>
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/login"
                            className="text-sm text-[#475569] hover:text-[#0F1F3D]"
                        >
                            Sign in
                        </Link>

                        <button
                            type="button"
                            onClick={() => setShowWaitlist(true)}
                            className="px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]"
                        >
                            Join Waitlist
                        </button>
                    </div>
                </div>
            </header>

            {/* Content */}
            <section className="max-w-7xl w-full mx-auto px-6 pt-32 pb-16 flex-1">
                <div className="mb-10 border-b border-[#E2E8F0] pb-8">
                    <span className="inline-flex items-center rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">
                        Legal
                    </span>

                    <h1 className="mt-5 text-5xl font-display font-bold tracking-tight">
                        Privacy Policy
                    </h1>

                    <p className="mt-3 text-[#64748B] text-sm">
                        LaunchRadar — operated by OneRoof Launchpad
                        <br />
                        Last updated: June 2026
                    </p>
                </div>

                <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-10 xl:gap-14">
                    {/* Sidebar Table of Contents */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#94A3B8] mb-4">
                                On this page
                            </p>
                            <nav className="max-h-[calc(100vh-140px)] overflow-y-auto pr-2">
                                <ul className="space-y-1 border-l border-[#E2E8F0]">
                                    {SECTIONS.map((section) => {
                                        const isActive = activeSection === section.id;
                                        return (
                                            <li key={section.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => scrollToSection(section.id)}
                                                    className={`block w-full text-left text-sm pl-4 py-1.5 -ml-px border-l-2 transition-colors ${
                                                        isActive
                                                            ? "border-[#0F1F3D] text-[#0F1F3D] font-medium"
                                                            : "border-transparent text-[#64748B] hover:text-[#0F1F3D] hover:border-[#CBD5E1]"
                                                    }`}
                                                >
                                                    {section.title}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        </div>
                    </aside>

                    {/* Main content */}
                    <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm p-8 md:p-12 space-y-6 text-sm">
                        <section id="introduction" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                1. Introduction
                            </h2>

                            <p className="text-[#475569]">
                                This Privacy Policy explains how OneRoof Launchpad ("ORL", "we", "us") collects, uses, stores, and shares personal data when you use LaunchRadar ("the Service").<br /><br />
                                ORL is registered in the Netherlands (KvK number: 98837567). ORL is the data controller under the EU General Data Protection Regulation (GDPR) and the Data Fiduciary under India's Digital Personal Data Protection Act, 2023 (DPDP Act) in relation to the personal data described in this policy.<br /><br />
                                The Service is operated from the Netherlands. Because ORL processes personal data of individuals in the European Economic Area and in India, this policy is designed to comply with both GDPR, as supplemented by the Dutch Uitvoeringswet AVG (UAVG) and the ePrivacy provisions of the Dutch Telecommunicatiewet, and the DPDP Act.<br /><br />
                                This policy should be read together with the LaunchRadar Terms and Conditions.
                            </p>
                        </section>

                        <section id="data-we-collect" className="space-y-3 scroll-mt-24">
                            <h2 className="text-3xl font-bold">
                                2. Data We Collect
                            </h2>

                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold">
                                    2.1 Account data
                                </h3>

                                <p className="text-[#475569] ">
                                    When you create an account, we collect the following information you
                                    provide during sign-up:
                                </p>

                                <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                    <li>Your full name</li>
                                    <li>Work email address</li>
                                    <li>Organisation name</li>
                                    <li>
                                        Password (stored only in hashed form; we never store or have access
                                        to your plain-text password)
                                    </li>
                                </ul>
                            </div>

                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold">
                                    2.2 Project configuration data
                                </h3>

                                <p className="text-[#475569] ">
                                    When you configure projects on the platform, we collect and store:

                                </p>

                                <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                    <li>Project names and descriptions</li>
                                    <li>Relevance briefs</li>
                                    <li>Keywords, sectors, geographies, and target entities</li>
                                    <li>Seed URLs you submit</li>
                                    <li>Delivery preferences (email, WhatsApp)</li>
                                </ul>
                            </div>

                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold">
                                    2.3 Communication data
                                </h3>

                                <p className="text-[#475569] ">
                                    If you enable email or WhatsApp delivery, we store the address or phone number you provide and a record of digests sent to you.
                                </p>
                            </div>

                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold">
                                    2.4 Usage and technical data
                                </h3>

                                <p className="text-[#475569] ">
                                    We collect usage and technical data automatically when you access the Service:
                                </p>

                                <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                    <li>Log data (IP address, browser type, device identifiers, timestamps)</li>
                                    <li>Pages visited, features used, and actions taken within the platform</li>
                                    <li>Cookies and similar technologies for session management and analytics (see Section 9)</li>
                                </ul>
                            </div>

                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold">
                                    2.5 Signal interaction data
                                </h3>

                                <p className="text-[#475569] ">
                                    We record how you interact with signals surfaced by the Service — for example, which signals you open, save, dismiss, or rate — to operate the platform and improve relevance scoring.
                                </p>
                            </div>
                        </section>

                        <section id="how-we-use-data" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                3. How We Use Your Data
                            </h2>

                            <p className="text-[#475569] ">
                                We process your personal data for the following purposes:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li>To provide and operate the Service, including running the signal pipeline against your project configuration</li>
                                <li>To deliver signal digests via in-app feed, email, and WhatsApp</li>
                                <li>To authenticate you and secure your account</li>
                                <li>To provide platform support, quality assurance, and abuse prevention</li>
                                <li>To improve the Service, including refining relevance scoring and pipeline performance</li>
                                <li>To communicate service announcements, security notices, and material changes to terms or this policy</li>
                                <li>To comply with legal obligations and respond to lawful requests from competent authorities</li>
                            </ul>
                        </section>

                        <section id="legal-bases" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                4. Legal Bases for Processing
                            </h2>

                            <p className="text-[#475569] ">
                                Under GDPR, we rely on the following legal bases:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li><strong>Contract:</strong> to provide the Service you have signed up for, including processing your account data, project configuration, and delivering signals.</li>
                                <li><strong>Legitimate interests:</strong> to secure the platform, prevent abuse, improve the Service, and conduct internal analytics. Our interests are balanced against your rights and freedoms.</li>
                                <li><strong>Consent:</strong> for WhatsApp delivery, optional marketing communications (if any), and non-essential cookies. You may withdraw consent at any time.</li>
                                <li><strong>Legal obligation:</strong> where processing is required to comply with applicable law.</li>
                            </ul>

                            <p className="text-[#475569] ">
                                Under the DPDP Act, we process the personal data of users in India on the basis of the consent you give at sign-up. We do not rely on the DPDP Act's "legitimate uses" as a basis for product improvement or analytics, those activities are based on your consent. Where processing is required to comply with a legal obligation or a lawful order, we rely on the applicable legitimate-use ground under Section 7 of the Act.
                            </p>
                        </section>

                        <section id="admin-visibility" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                5. Admin Visibility of Project Data
                            </h2>

                            <p className="text-[#475569] ">
                                As disclosed in Section 4 of the Terms and Conditions, ORL personnel with production database access are able to view all data stored on the platform. In practice this means our administrators can access:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li>Your account email and organisation</li>
                                <li>Your project names, descriptions, and relevance briefs</li>
                                <li>Keywords, sectors, geographies, seed URLs, and target entities you have added</li>
                                <li>Your usage activity, credits used, and signal history</li>
                            </ul>

                            <p className="text-[#475569] ">
                                This access exists strictly for platform support, quality assurance, abuse prevention, and service improvement. ORL does not sell or share your project data with third parties for commercial purposes.<br /><br />
                                Administrative access is restricted to authorised personnel bound by confidentiality obligations, and is logged.
                            </p>
                        </section>

                        <section id="ai-processing" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                6. AI Processing and Third-Party Sub-Processors
                            </h2>

                            <p className="text-[#475569] ">
                                LaunchRadar uses artificial intelligence to generate summaries, relevance scores, and recommended next steps. In accordance with Article 50 of the EU AI Act, we disclose that signal summaries, relevance scores, and recommended next steps produced by the Service are generated by an artificial intelligence system.<br /><br />
                                To produce these outputs, elements of your project configuration, including keywords, sector descriptions, and relevance briefs, are transmitted to a third-party AI infrastructure provider for processing.<br /><br />
                                ORL does not transmit personally identifiable information to its AI provider beyond what is contained in your project configuration. You should avoid entering personal data (such as individual names or personal contact details) into project fields unless necessary for your monitoring purpose.<br /><br />
                                We rely on contractual safeguards, including data processing agreements and, where applicable, Standard Contractual Clauses, to ensure that any sub-processor we use meets standards consistent with applicable data protection law, including GDPR and the DPDP Act.
                            </p>

                            <p className="text-[#475569] font-semibold">
                                Categories of sub-processors we currently use:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li>AI infrastructure provider and their Data Processing Addendum, incorporating the EU Standard Contractual Clauses, are automatically incorporated into its Commercial Terms of Service and applies to our use of the service.</li>
                                <li>Cloud hosting and infrastructure providers (for platform operation and data storage)</li>
                                <li>Email delivery providers (for transactional and digest emails)</li>
                                <li>WhatsApp Business API providers (where WhatsApp delivery is enabled)</li>
                                <li>Analytics and error-tracking providers (where applicable)</li>
                            </ul>

                            <p className="text-[#475569] ">
                                ORL will update this Privacy Policy to reflect any material changes to the sub-processors it uses. An up-to-date list is available on request via the contact address in Section 13.
                            </p>
                        </section>

                        <section id="international-transfers" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                7. International Data Transfers

                            </h2>

                            <p className="text-[#475569] ">
                                Some of our sub-processors are located outside the European Economic Area (EEA), including in India, the United States, and other jurisdictions. Where personal data is transferred outside the EEA, we rely on appropriate safeguards under GDPR, typically Standard Contractual Clauses approved by the European Commission, or transfers to jurisdictions covered by an adequacy decision.<br /><br />
                                Where personal data of individuals in India is transferred outside India, we transfer it only to jurisdictions not restricted by the Government of India under the DPDP Act, and we maintain contractual safeguards with the recipient.<br /><br />
                                You may request information about the specific safeguards in place by contacting us at the address in Section 13.
                            </p>
                        </section>

                        <section id="data-retention" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                8. Data Retention
                            </h2>

                            <p className="text-[#475569] ">
                                We retain personal data only as long as necessary for the purposes set out in Section 3, and we erase it once that purpose is served or you withdraw consent, unless we are required by law to retain it for longer.
                            </p>

                            <p className="text-[#475569]">
                                The specific retention periods are:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li><strong>Account data (name, email, organisation, hashed password):</strong> retained for as long as your account is active, and deleted within 30 days of account closure.</li>
                                <li><strong>Project configuration (project names, briefs, keywords, sectors, seed URLs, entities, delivery preferences):</strong> retained for the life of the account, and deleted within 30 days of account closure.</li>
                                <li><strong>Signal history (signals surfaced to you and your interactions with them):</strong> retained for the life of the account, and deleted within 30 days of account closure.</li>
                                <li><strong>Usage and log data (IP address, device identifiers, page and feature usage):</strong> retained for up to 12 months, after which it is either deleted or aggregated into non-identifying statistics.</li>
                                <li><strong>Communication records (support tickets, email or WhatsApp digest delivery logs, correspondence with us):</strong> retained for 24 months from the date of the last interaction, unless a longer period is required to resolve an open matter or meet a legal obligation.</li>
                                <li><strong>Consent records (record that you gave, updated, or withdrew consent, and when):</strong> retained for the life of the account and for a further 3 years, as evidence of lawful processing.</li>
                                <li><strong>Cookies:</strong> retained for the periods specified in our cookie banner, typically no longer than 12 months for non-essential cookies.</li>
                            </ul>

                            <p className="text-[#475569] ">
                                <b>When you withdraw consent or delete your account</b>, we erase the associated personal data within 30 days, in line with GDPR Article 17 and Section 8(7) of the DPDP Act. Data present in encrypted backups may persist for up to a further 90 days until backups are rotated, after which it is overwritten. During that window, backup data is not used for any purpose other than disaster recovery.
                            </p>

                            <p className="text-[#475569] ">
                                <b>Statutory retention floors.</b> Some data must be kept for longer to comply with law:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li>Under Dutch law: tax and financial records for 7 years (Algemene wet inzake rijksbelastingen, Burgerlijk Wetboek Boek 2 Art. 10); VAT-related records for 7 years, or 10 years where property is involved.</li>
                                <li>Under Indian law: books of account for 8 years (Companies Act 2013), income tax records for 8 years from the end of the relevant assessment year, GST records for 6 years, and log data as required under CERT-In directions.</li>
                            </ul>

                            <p className="text-[#475569] ">
                                Where a statutory retention period applies, the data is retained only for that purpose and is not used for any other processing during that period
                            </p>
                        </section>

                        <section id="cookies" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                9. Cookies and Similar Technologies
                            </h2>

                            <p className="text-[#475569] ">
                                LaunchRadar uses cookies and similar technologies to:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li>Keep you signed in (essential cookies)</li>
                                <li>Remember your preferences</li>
                                <li>Understand how the Service is used (analytics)</li>
                            </ul>

                            <p className="text-[#475569] ">
                                Essential cookies do not require consent. Non-essential cookies (including analytics) are set only with your consent. You can grant, refuse, or withdraw cookie consent at any time via our cookie banner or your browser settings. Refusing non-essential cookies does not affect your ability to use the Service.
                            </p>


                        </section>

                        <section id="electronic-communications" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                10. Electronic Communications and WhatsApp Delivery
                            </h2>

                            <p className="text-[#475569] ">
                                Where you choose to receive your signal digest by email or WhatsApp, we send those messages on the basis of your explicit opt-in consent. You may withdraw consent at any time via your project settings, by using the unsubscribe mechanism included in every message, or by contacting us at the address in Section 14. Withdrawing consent does not affect the lawfulness of processing carried out before withdrawal.<br /><br />
                                We do not use email or WhatsApp to send you unrelated marketing without your separate, prior consent.
                            </p>
                        </section>

                        <section id="your-rights" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                11. Your Rights
                            </h2>

                            <p className="text-[#475569] ">
                                Under GDPR, you have the following rights in relation to your personal data:
                            </p>

                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li><b>Access:</b> request a copy of the personal data we hold about you.</li>
                                <li><b>Rectification:</b> request correction of inaccurate or incomplete data.</li>
                                <li><b>Erasure:</b> request deletion of your personal data, subject to legal retention requirements.</li>
                                <li><b>Restriction:</b> request that we limit how we process your data.</li>
                                <li><b>Portability:</b> request your data in a structured, commonly used, machine-readable format.</li>
                                <li><b>Objection:</b> object to processing based on legitimate interests.</li>
                                <li><b>Withdraw consent:</b> where processing is based on consent, withdraw it at any time without affecting the lawfulness of prior processing.</li>
                                <li><b>Lodge a complaint:</b> with a supervisory authority. In the Netherlands, this is the Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl).</li>
                            </ul>

                            <p className="text-[#475569] ">
                                Under the DPDP Act, if you are located in India, you additionally have the right to:
                            </p>



                            <ul className="list-disc pl-6 space-y-2 text-[#475569] ">
                                <li>Obtain a summary of the personal data being processed and the processing activities undertaken</li>
                                <li>Correct, complete, update, or erase your personal data</li>
                                <li>Nominate another individual to exercise your rights in the event of your death or incapacity</li>
                                <li>Readily available means of grievance redressal (see Section 14)</li>
                            </ul>

                            <p className="text-[#475569] ">
                                LaunchRadar does not use automated decision-making, including profiling, that produces legal or similarly significant effects on individuals. Our relevance scoring is applied to content, not to individuals.<br /><br />
                                To exercise any of these rights, contact us at the address in Section 14. We will respond within one month of receiving your request, as required by GDPR, and within the timelines required by the DPDP Act.
                            </p>
                        </section>

                        <section id="security-breach" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                12. Security and Breach Notification
                            </h2>

                            <p className="text-[#475569] ">
                                We implement appropriate technical and organisational measures to protect personal data against unauthorised access, alteration, disclosure, or destruction. These include encryption in transit, access controls, logging, and regular review of our security practices.<br /><br />
                                In the event of a personal data breach, we will notify the relevant supervisory authority, in the EEA, the Autoriteit Persoonsgegevens; in India, the Data Protection Board and affected users in accordance with GDPR and the DPDP Act. Where required, we also report cybersecurity incidents to CERT-In in accordance with applicable Indian law.<br /><br />
                                No system is completely secure. You are responsible for maintaining the confidentiality of your account credentials. If you suspect unauthorized access to your account, contact us immediately.

                            </p>


                        </section>

                        <section id="children" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                13. Children
                            </h2>

                            <p className="text-[#475569] ">
                                LaunchRadar is an intelligence monitoring tool for business, professional, and personal use. It is not directed at children. We do not knowingly collect personal data from individuals under 16 (as required under GDPR / UAVG in the Netherlands) or under 18 (as required under the DPDP Act in India) without verifiable parental or lawful guardian consent.<br /><br />
                                We do not undertake tracking, behavioural monitoring, or targeted advertising directed at children. If you believe a child has provided personal data to us, contact us and we will delete it.
                            </p>


                        </section>

                        <section id="contact-grievance" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                14. Contact and Grievance Redressal
                            </h2>

                            <p className="text-[#475569] leading-relaxed">
                                For questions about this Privacy Policy, to exercise your rights, or to raise a data protection concern, contact:<br />
                                OneRoof Launchpad<br />
                                Netherlands<br />
                                <a href="mailto:info@launchradar.live" className="text-[#0EA5E9] hover:underline">info@launchradar.live</a><br />
                                For users in India, the above contact also serves as the point of contact for grievance redressal under the DPDP Act. If you are not satisfied with our response, you may escalate your complaint to the Data Protection Board of India.<br />
                                For users in the EEA, if you are not satisfied with our response, you may lodge a complaint with the Autoriteit Persoonsgegevens or your local supervisory authority.
                            </p>


                        </section>

                        <section id="changes" className="space-y-3 scroll-mt-24">
                            <h2 className="text-2xl font-semibold">
                                15. Changes to This Policy

                            </h2>

                            <p className="text-[#475569] ">
                                We may update this Privacy Policy from time to time. Where changes are material, we will notify you by email at least 14 days before the changes take effect. The current version of this policy is always available within the Service.<br /><br />
                                By using LaunchRadar, you confirm that you have read and understood this  Privacy Policy.
                            </p>
                        </section>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#E2E8F0] py-10">
                <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-[#64748B]">
                    <p>© 2026 LaunchRadar. All rights reserved.</p>

                    <div className="flex items-center gap-6">
                        <Link
                            to="/privacy-policy"
                            className="text-[#0F1F3D] font-medium"
                        >
                            Privacy Policy
                        </Link>

                        <Link
                            to="/terms-and-conditions"
                            className="hover:text-[#0F1F3D]"
                        >
                            Terms & Conditions
                        </Link>

                        <Link
                            to="/faq"
                            className="hover:text-[#0F1F3D]"
                        >
                            FAQs
                        </Link>

                        <Link
                            to="/contact"
                            className="hover:text-[#0F1F3D]"
                        >
                            Contact
                        </Link>
                    </div>
                </div>
            </footer>

            {showWaitlist && (
                <WaitlistModal
                    onClose={() => setShowWaitlist(false)}
                />
            )}
        </div>
    );
}